import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/lna.png';
import '../css/MemberPortal.css';

const PAYMENT_METHOD_LABELS = {
  mpesa: 'M-Pesa',
  ecocash: 'EcoCash',
  bank: 'Bank Transfer',
};

const MemberPortal = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const fetchPerformance = useCallback(async () => {
    try {
      const res = await api.get('/portal/performance');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const navbar = document.querySelector('.navbar');
    if (navbar) navbar.style.display = 'none';
    document.body.classList.add('admin-mode');

    fetchPerformance();

    return () => {
      if (navbar) navbar.style.display = '';
      document.body.classList.remove('admin-mode');
    };
  }, [fetchPerformance]);

  const handleLogout = () => {
    logout();
    navigate('/memberLogin');
  };

  if (loading) {
    return (
      <div className="portal-loading">
        <div className="portal-spinner" />
        <p>Loading your portal...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-error">
        <p>{error}</p>
        <button onClick={fetchPerformance} className="portal-btn">Retry</button>
      </div>
    );
  }

  const { member, performance, monthlyStatus, recentTransactions } = data;
  const statusClass = performance.status === 'up_to_date' ? 'status-good'
    : performance.status === 'overdue' ? 'status-overdue' : 'status-pending';

  return (
    <div className="member-portal">
      <header className="portal-header">
        <div className="portal-header-left">
          <img src={logo} alt="LeNMO" className="portal-logo" />
          <div>
            <h1>Member Portal</h1>
            <p>Lesotho Nursing and Midwifery Organisation</p>
          </div>
        </div>
        <div className="portal-header-right">
          <span className="portal-user">{member.title}. {member.name}</span>
          <button onClick={handleLogout} className="portal-btn portal-btn-outline">Logout</button>
        </div>
      </header>

      <main className="portal-main">
        <section className="portal-welcome">
          <h2>Welcome, {member.name}</h2>
          <p>Employment #: <strong>{member.employment_number}</strong> | {member.employer_name} | {member.duty_station}</p>
        </section>

        <section className="portal-stats">
          <div className={`portal-stat-card ${statusClass}`}>
            <div className="portal-stat-icon">
              {performance.status === 'up_to_date' ? '✅' : performance.status === 'overdue' ? '⚠️' : '⏳'}
            </div>
            <div>
              <h3>Payment Status</h3>
              <p className="portal-stat-value">
                {performance.status === 'up_to_date' ? 'Up to Date' :
                 performance.status === 'overdue' ? 'Overdue' : 'Pending'}
              </p>
              <span className="portal-stat-sub">Current month: {performance.currentMonth}</span>
            </div>
          </div>

          <div className="portal-stat-card">
            <div className="portal-stat-icon">📊</div>
            <div>
              <h3>Payment Rate</h3>
              <p className="portal-stat-value">{performance.paymentRate}%</p>
              <span className="portal-stat-sub">{performance.totalTransactions} of {performance.monthsSinceApproval} months</span>
            </div>
          </div>

          <div className="portal-stat-card">
            <div className="portal-stat-icon">💰</div>
            <div>
              <h3>Total Paid</h3>
              <p className="portal-stat-value">M{performance.totalPaid.toFixed(2)}</p>
              <span className="portal-stat-sub">{performance.totalTransactions} transactions</span>
            </div>
          </div>

          <div className="portal-stat-card">
            <div className="portal-stat-icon">📋</div>
            <div>
              <h3>Membership</h3>
              <p className="portal-stat-value">{member.payment_frequency || 'Monthly'}</p>
              <span className="portal-stat-sub">via {member.payment_method || 'N/A'}</span>
            </div>
          </div>
        </section>

        <section className="portal-section">
          <h3>Monthly Payment History</h3>
          <div className="portal-monthly-grid">
            {monthlyStatus.map((m) => (
              <div key={m.month} className={`portal-month-card ${m.paid ? 'paid' : 'unpaid'}`}>
                <span className="portal-month-label">{m.month}</span>
                {m.paid ? (
                  <>
                    <span className="portal-month-status paid">Paid</span>
                    <span className="portal-month-amount">M{m.amount?.toFixed(2)}</span>
                    <span className="portal-month-method">{PAYMENT_METHOD_LABELS[m.payment_method] || m.payment_method}</span>
                  </>
                ) : (
                  <span className="portal-month-status unpaid">Not Paid</span>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="portal-section">
          <h3>Recent Transactions</h3>
          {recentTransactions.length === 0 ? (
            <p className="portal-empty">No transactions recorded yet.</p>
          ) : (
            <div className="portal-table-wrapper">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Month</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{tx.transaction_date}</td>
                      <td>{tx.payment_month}</td>
                      <td>M{parseFloat(tx.amount).toFixed(2)}</td>
                      <td>{PAYMENT_METHOD_LABELS[tx.payment_method] || tx.payment_method}</td>
                      <td>{tx.reference_number || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="portal-section portal-payment-info">
          <h3>How to Pay</h3>
          <div className="portal-payment-methods">
            <div className="portal-payment-card">
              <h4>M-Pesa</h4>
              <p>Paybill: <strong>45799</strong></p>
              <p>Account: <strong>{member.employment_number}</strong></p>
            </div>
            <div className="portal-payment-card">
              <h4>EcoCash</h4>
              <p>Contact administration for EcoCash payment details.</p>
            </div>
            <div className="portal-payment-card">
              <h4>Bank Transfer</h4>
              <p>Standard Lesotho Bank</p>
              <p>Account: <strong>9080003206161</strong></p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MemberPortal;
